import { ApolloError } from '@apollo/client';
import { IonToast, ToastButton, useIonToast } from '@ionic/react';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { i18nKeyType } from '../../i18n';
import { formatErrorMessage } from '../../util/error';
import { useIsMounted, usePrevious } from '../../util/hooks';

interface GqlSaveProps {
  loading?: boolean;
  error?: ApolloError | boolean | string;
  actions?: ToastButton[];
  duration?: number;
  defaultErrorMessageKey?: i18nKeyType;
  defaultMessageKey?: i18nKeyType;
  showOnly?: 'success' | 'error';
  onDidDismiss?: () => void;
  position?: 'top' | 'bottom' | 'middle';
}

const SaveStatusToast = (props: GqlSaveProps): ReactElement => {
  const {
    error,
    loading,
    actions,
    duration,
    defaultErrorMessageKey: defaultErrorMesssageKey,
    defaultMessageKey,
    showOnly,
    onDidDismiss,
    position = 'bottom',
  } = props;
  const { i18n } = useTranslation();

  const errorMessage = useMemo(() => {
    if (error) {
      return formatErrorMessage(error, defaultErrorMesssageKey);
    }
  }, [defaultErrorMesssageKey, error]);

  const isMounted = useIsMounted();

  const [isSavePending, setSavePending] = useState<boolean>(false);
  const wasSavePending = usePrevious(isSavePending);

  useEffect(() => {
    if (loading) {
      if (isMounted()) {
        setSavePending(true);
      }
    }
  }, [loading, isMounted]);

  /**
   * Separate effect to handle error state
   *
   * We immediately set the save pending to false to clear out the api success state
   * and prevent the success toast from potentially appearing when it shouldn't.
   *
   * We cannot do this in the same effect as the one setting the save pending
   * state to true since the success toast depends on the api success state.
   * (Would cause premature closure of the success toast)
   */
  useEffect(() => {
    if (error && !loading) {
      if (isMounted()) {
        setSavePending(false);
      }
    }
  }, [error, loading, isMounted]);

  const apiCompleted = wasSavePending === true && loading === false;
  const apiSuccess = apiCompleted && (error === undefined || error === false);
  const showSuccess = showOnly === undefined || showOnly === 'success';
  const showError = showOnly === undefined || showOnly === 'error';

  return (
    <>
      <IonToast
        isOpen={apiSuccess && showSuccess}
        onDidDismiss={() => {
          if (isMounted()) {
            setSavePending(false);
          }

          if (onDidDismiss) {
            onDidDismiss();
          }
        }}
        color={'success'}
        position={position}
        message={
          defaultMessageKey
            ? i18n.t(defaultMessageKey)
            : i18n.t('common:save_success')
        }
        duration={duration ?? 1500}
      />
      <IonToast
        isOpen={(errorMessage ? true : false) && showError}
        onDidDismiss={() => {
          if (isMounted()) {
            setSavePending(false);
          }

          if (onDidDismiss) {
            onDidDismiss();
          }
        }}
        color={'danger'}
        position={position}
        message={errorMessage}
        duration={duration ?? 3500}
        buttons={actions}
      />
    </>
  );
};

const useSaveStatusToast = (props: GqlSaveProps) => {
  const {
    error,
    loading,
    actions,
    duration,
    defaultErrorMessageKey: defaultErrorMesssageKey,
    defaultMessageKey,
  } = props;
  const { i18n } = useTranslation();

  const errorMessage = useMemo(() => {
    if (error) {
      return formatErrorMessage(error, defaultErrorMesssageKey);
    }
  }, [defaultErrorMesssageKey, error]);

  const isMounted = useIsMounted();

  const [isSavePending, setSavePending] = useState<boolean>(false);
  const wasSavePending = usePrevious(isSavePending);

  useEffect(() => {
    if (loading) {
      if (isMounted()) {
        setSavePending(true);
      }
    }
  }, [loading, isMounted]);

  const apiCompleted = wasSavePending === true && loading === false;
  const apiSuccess = apiCompleted && (error === undefined || error === false);

  const [presentSuccessToast] = useIonToast();
  const [presentErrorToast] = useIonToast();

  useEffect(() => {
    if (apiSuccess) {
      presentSuccessToast({
        color: 'success',
        message: defaultMessageKey
          ? i18n.t(defaultMessageKey)
          : i18n.t('common:save_success'),
        duration: duration ?? 1500,
        position: 'bottom',
      });
    }

    if (errorMessage !== undefined) {
      presentErrorToast({
        buttons: actions,
        color: 'danger',
        duration: duration ?? 3500,
        message: errorMessage,
        onDidDismiss: () => {
          if (isMounted()) {
            setSavePending(false);
          }
        },
        position: 'bottom',
      });
    }
  }, [
    actions,
    apiSuccess,
    defaultMessageKey,
    duration,
    errorMessage,
    i18n,
    isMounted,
    presentErrorToast,
    presentSuccessToast,
  ]);
};

export { SaveStatusToast, useSaveStatusToast };
